import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Zap, Search as SearchIcon, Shield, Sparkles, Tag, Truck, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import heroExplore from "@/assets/hero-explore.webp";
import heroSell from "@/assets/hero-sell.webp";
import { SITE_CONFIG } from "@/config/site-meta";
import heroDgt from "@/assets/hero-dgt.webp";

const ICON_MAP: Record<string, any> = {
  Search: SearchIcon,
  Zap,
  Shield,
  Sparkles,
  Tag,
  Truck,
  Star,
};

type Slide = {
  title: string;
  subtitle: string;
  cta: string;
  ctaLink: string;
  icon: string;
  image: string;
};

const DEFAULT_SLIDES: Slide[] = [
   { title: `Compra y vende patinetes en ${SITE_CONFIG.name}`, subtitle: "El marketplace #1 en España. Nuevos, segunda mano y recambios al mejor precio.", cta: "Explorar patinetes", ctaLink: "/buscar", icon: "Search", image: heroExplore },
  { title: "¿Tienes un patinete que no usas?", subtitle: "Publícalo en minutos y llega a miles de compradores en toda España.", cta: "Vender ahora", ctaLink: "/publicar", icon: "Zap", image: heroSell },
  { title: "Solo patinetes homologados DGT", subtitle: "Filtra por vehículos con certificación DGT y circula tranquilo y legal.", cta: "Ver homologados", ctaLink: "/buscar?dgt=1", icon: "Shield", image: heroDgt },
];

const FALLBACK_IMAGES = [heroExplore, heroSell, heroDgt];

const HeroBanner = () => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slides, setSlides] = useState<Slide[]>(DEFAULT_SLIDES);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "hero_slides")
        .maybeSingle();
      if (cancelled || !data?.value) return;
      const arr = data.value as any;
      if (Array.isArray(arr) && arr.length > 0) {
        const merged: Slide[] = arr.map((s: any, i: number) => ({
          title: s.title || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].title,
          subtitle: s.subtitle || "",
          cta: s.cta || "Ver más",
          ctaLink: s.ctaLink || "/buscar",
          icon: s.icon || "Search",
          image: s.image && s.image.trim() ? s.image : FALLBACK_IMAGES[i % FALLBACK_IMAGES.length],
        }));
        setSlides(merged);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo, slides.length]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo, slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, slides.length]);

  const slide = slides[current] ?? slides[0];
  const Icon = ICON_MAP[slide.icon] ?? SearchIcon;

  return (
    <section className="relative overflow-hidden h-[480px] md:h-[560px]">
      {slides.map((s, i) => (
        <div
          key={i}
          className={cn(
            "absolute inset-0 overflow-hidden transition-opacity duration-700",
            i === current ? "opacity-100" : "opacity-0"
          )}
        >
          <img
            src={s.image}
            alt={s.title}
            width={1920}
            height={1024}
            loading={i === 0 ? "eager" : "lazy"}
            decoding={i === 0 ? "sync" : "async"}
            fetchPriority={i === 0 ? "high" : "low"}
            style={{ transform: `translate3d(0, ${scrollY * 0.25}px, 0) scale(1.08)` }}
            className={cn(
              "h-full w-full object-cover will-change-transform",
              i === current ? "animate-ken-burns" : ""
            )}
          />
        </div>
      ))}

      <div
        className="absolute inset-0 bg-gradient-to-r from-background/85 via-background/60 to-background/30"
        style={{ transform: `translate3d(0, ${scrollY * 0.1}px, 0)` }}
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(var(--primary)/0.15),transparent_60%)]"
        style={{ transform: `translate3d(0, ${scrollY * 0.05}px, 0)` }}
      />

      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="hidden md:flex absolute left-4 top-1/2 z-10 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-background/60 backdrop-blur-sm text-foreground transition-all hover:bg-background/90 md:left-8"
            aria-label="Anterior"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            className="hidden md:flex absolute right-4 top-1/2 z-10 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-background/60 backdrop-blur-sm text-foreground transition-all hover:bg-background/90 md:right-8"
            aria-label="Siguiente"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <div className="container relative flex h-full items-center md:px-20 lg:px-24 xl:px-28">
        <div className={cn(
          "max-w-2xl transition-all duration-500",
          isTransitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
        )}>
          <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 backdrop-blur-sm text-primary shadow-lg shadow-primary/10">
            <Icon className="h-7 w-7" />
          </div>
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-foreground md:text-5xl drop-shadow-sm">
            {slide.title}
          </h1>
          <p className="mt-4 text-lg text-foreground/80 md:text-xl max-w-xl">
            {slide.subtitle}
          </p>
          <Button size="lg" className="mt-8" asChild>
            <Link to={slide.ctaLink}>
              <Icon className="mr-2 h-4 w-4" /> {slide.cta}
            </Link>
          </Button>
        </div>
      </div>

      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center justify-center gap-3 z-10">
          <button
            onClick={prev}
            className="md:hidden flex h-8 w-8 items-center justify-center rounded-full bg-background/70 backdrop-blur-sm text-foreground transition-all hover:bg-background/90"
            aria-label="Anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={cn(
                "h-2 rounded-full transition-all duration-300 relative before:absolute before:inset-0 before:-m-3 before:content-['']",
                i === current ? "w-8 bg-primary" : "w-2 bg-foreground/30 hover:bg-foreground/50"
              )}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
          <button
            onClick={next}
            className="md:hidden flex h-8 w-8 items-center justify-center rounded-full bg-background/70 backdrop-blur-sm text-foreground transition-all hover:bg-background/90"
            aria-label="Siguiente"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </section>
  );
};

export default HeroBanner;
